import { useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useDebounce } from '@/hooks/useDebounce'
import { ALL_COUNTRY_REGION_CODES } from '@/lib/constants'

export type UseAutocompleteSuggestionsReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[]
  isLoading: boolean
  isError: boolean
  resetSession: () => void
}

/**
 * Fetches Google Places Autocomplete suggestions (Autocomplete Data API).
 * Input is debounced before each request. Requires an ancestor APIProvider.
 * Failures degrade to an empty suggestion list.
 */
export function useAutocompleteSuggestions(
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
  debounceMs = 300,
  /** When false, skips API calls (e.g. field inactive or dropdown dismissed). */
  fetchEnabled = true
): UseAutocompleteSuggestionsReturn {
  const placesLib = useMapsLibrary('places')
  const queryClient = useQueryClient()
  const debouncedInput = useDebounce(inputString, debounceMs)
  const query = debouncedInput.trim()

  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const primaryTypesKey = (requestOptions.includedPrimaryTypes ?? []).join(',')

  const { data, isFetching, isError } = useQuery({
    queryKey: ['places-autocomplete', query, primaryTypesKey],
    queryFn: async () => {
      if (!placesLib) return []

      const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionToken()
      }

      const request: google.maps.places.AutocompleteRequest = {
        ...requestOptions,
        includedRegionCodes:
          requestOptions.includedRegionCodes ?? ALL_COUNTRY_REGION_CODES,
        input: query,
        sessionToken: sessionTokenRef.current,
      }

      const res =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      return res.suggestions
    },
    enabled: fetchEnabled && !!placesLib && query.length >= 3,
    staleTime: 0,
    retry: false,
  })

  return {
    suggestions:
      fetchEnabled && query.length >= 3 && !isError ? (data ?? []) : [],
    isLoading: fetchEnabled && isFetching,
    isError,
    resetSession: () => {
      sessionTokenRef.current = null
      queryClient.removeQueries({ queryKey: ['places-autocomplete'] })
    },
  }
}
